import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, Eye, EyeOff, Shield, CheckCircle, ArrowLeft } from 'lucide-react';
import httpClient from '../../Api/axios';

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [shake, setShake] = useState(false);

  const isVerified = location.state?.isVerified || false;
  const email = location.state?.email || '';

  useEffect(() => {
    const isVerifiedFromStorage = localStorage.getItem('isPasswordResetVerified') === 'true';
    const storedEmail = localStorage.getItem('resetEmail');
    
    if (!isVerified && !isVerifiedFromStorage) {
      navigate('/admin/forgot-password');
    }
    
    if (!email && storedEmail) {
      // We'll use the stored email in the API call
    }
  }, [isVerified, navigate, email]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setShake(false);

    if (!newPassword.trim() || !confirmPassword.trim()) {
      setError('Please enter both password fields');
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }

    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumber = /[0-9]/.test(newPassword);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);

    if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar) {
      setError('Password must contain uppercase, lowercase, number, and special character');
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }

    setLoading(true);

    try {
      const resetEmail = email || localStorage.getItem('resetEmail');
      
      // ✅ FIXED: Added /api prefix
      const response = await httpClient.post('/api/admin/reset-password', {
        email: resetEmail,
        newPassword,
        confirmPassword
      });

      if (response.data.success) {
        setSuccess('Password reset successfully!');
        
        localStorage.removeItem('isPasswordResetVerified');
        localStorage.removeItem('resetEmail');
        
        setTimeout(() => {
          navigate('/admin');
        }, 2000);
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setError(error.response?.data?.message || 'Failed to reset password');
      setShake(true);
      setTimeout(() => setShake(false), 500);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    localStorage.removeItem('isPasswordResetVerified');
    localStorage.removeItem('resetEmail');
    navigate('/admin/forgot-password');
  };

  const getPasswordStrength = () => {
    if (!newPassword) return { strength: 0, label: 'Enter a password', color: '#4ade80' };
    
    let score = 0;
    if (newPassword.length >= 8) score++;
    if (/[A-Z]/.test(newPassword)) score++;
    if (/[a-z]/.test(newPassword)) score++;
    if (/[0-9]/.test(newPassword)) score++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)) score++;
    
    const strengths = [
      { label: 'Very Weak', color: '#ef4444' },
      { label: 'Weak', color: '#f97316' },
      { label: 'Fair', color: '#eab308' },
      { label: 'Good', color: '#22c55e' },
      { label: 'Strong', color: '#22c55e' },
      { label: 'Very Strong', color: '#22c55e' }
    ];
    
    return { strength: score, ...strengths[score] };
  };

  const passwordStrength = getPasswordStrength();

  return (
    <div className="reset-password-container">
      <div className="login-bg">
        <div className="bg-blob blob-1"></div>
        <div className="bg-blob blob-2"></div>
        <div className="bg-blob blob-3"></div>
      </div>

      <div className={`reset-card ${shake ? 'shake' : ''}`}>
        <button className="back-btn" onClick={handleBack}>
          <ArrowLeft size={20} />
          Back
        </button>

        <div className="reset-header">
          <div className="reset-logo">
            <Shield size={40} className="logo-icon" />
            <span className="logo-text">Set New Password</span>
          </div>
          
          <h2 className="reset-title">Create New Password</h2>
          <p className="reset-subtitle">
            Enter your new password for <strong>{email || 'your account'}</strong>
          </p>
        </div>

        <form onSubmit={handleResetPassword} className="reset-form">
          <div className="form-group">
            <label className="form-label">New Password</label>
            <div className="input-wrapper">
              <Lock size={20} className="input-icon" />
              <input
                type={showPassword ? 'text' : 'password'}
                className="form-input"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            
            {newPassword && (
              <div className="password-strength">
                <div className="strength-bar">
                  <div 
                    className="strength-fill" 
                    style={{ 
                      width: `${(passwordStrength.strength / 5) * 100}%`,
                      backgroundColor: passwordStrength.color
                    }}
                  ></div>
                </div>
                <span style={{ color: passwordStrength.color }} className="strength-label">
                  {passwordStrength.label}
                </span>
              </div>
            )}

            <div className="password-hint">
              <span>Password must contain:</span>
              <ul>
                <li className={newPassword.length >= 8 ? 'valid' : ''}>
                  {newPassword.length >= 8 ? '✓' : '○'} At least 8 characters
                </li>
                <li className={/[A-Z]/.test(newPassword) ? 'valid' : ''}>
                  {/[A-Z]/.test(newPassword) ? '✓' : '○'} Uppercase letter
                </li>
                <li className={/[a-z]/.test(newPassword) ? 'valid' : ''}>
                  {/[a-z]/.test(newPassword) ? '✓' : '○'} Lowercase letter
                </li>
                <li className={/[0-9]/.test(newPassword) ? 'valid' : ''}>
                  {/[0-9]/.test(newPassword) ? '✓' : '○'} Number
                </li>
                <li className={/[!@#$%^&*(),.?":{}|<>]/.test(newPassword) ? 'valid' : ''}>
                  {/[!@#$%^&*(),.?":{}|<>]/.test(newPassword) ? '✓' : '○'} Special character
                </li>
              </ul>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Confirm Password</label>
            <div className="input-wrapper">
              <Lock size={20} className="input-icon" />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                className="form-input"
                placeholder="Confirm your new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={loading}
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {newPassword && confirmPassword && newPassword !== confirmPassword && (
              <div className="password-match-error">
                ⚠️ Passwords do not match
              </div>
            )}
            {newPassword && confirmPassword && newPassword === confirmPassword && (
              <div className="password-match-success">
                ✓ Passwords match
              </div>
            )}
          </div>

          {error && (
            <div className="error-message">
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="success-message">
              <CheckCircle size={18} />
              <span>{success}</span>
            </div>
          )}

          <button
            type="submit"
            className="reset-btn"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Resetting Password...
              </>
            ) : (
              'Reset Password'
            )}
          </button>
        </form>
      </div>

      <style jsx>{`
        .reset-password-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #0f0c29, #302b63, #24243e);
          position: relative;
          overflow: hidden;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          padding: 20px;
        }

        .login-bg {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          overflow: hidden;
          z-index: 0;
        }

        .bg-blob {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.3;
          animation: float 20s ease-in-out infinite;
        }

        .blob-1 {
          width: 500px;
          height: 500px;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          top: -150px;
          right: -100px;
          animation-delay: 0s;
        }

        .blob-2 {
          width: 400px;
          height: 400px;
          background: linear-gradient(135deg, #ec4899, #8b5cf6);
          bottom: -100px;
          left: -100px;
          animation-delay: -5s;
        }

        .blob-3 {
          width: 300px;
          height: 300px;
          background: linear-gradient(135deg, #6366f1, #3b82f6);
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          animation-delay: -10s;
        }

        @keyframes float {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          25% {
            transform: translate(50px, -30px) scale(1.1);
          }
          50% {
            transform: translate(-20px, 40px) scale(0.9);
          }
          75% {
            transform: translate(30px, -20px) scale(1.05);
          }
        }

        .reset-card {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 24px;
          padding: 48px 40px;
          width: 100%;
          max-width: 460px;
          position: relative;
          z-index: 1;
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4);
          animation: slideUp 0.6s ease-out;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(40px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .reset-card.shake {
          animation: shake 0.5s ease-in-out;
        }

        @keyframes shake {
          0%, 100% {
            transform: translateX(0);
          }
          10%, 30%, 50%, 70%, 90% {
            transform: translateX(-8px);
          }
          20%, 40%, 60%, 80% {
            transform: translateX(8px);
          }
        }

        .back-btn {
          background: none;
          border: none;
          color: rgba(255, 255, 255, 0.5);
          font-size: 14px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
          padding: 8px 0;
          margin-bottom: 16px;
        }

        .back-btn:hover {
          color: #8b5cf6;
        }

        .reset-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .reset-logo {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-bottom: 20px;
        }

        .logo-icon {
          color: #8b5cf6;
          filter: drop-shadow(0 0 20px rgba(139, 92, 246, 0.3));
        }

        .logo-text {
          font-size: 22px;
          font-weight: 700;
          background: linear-gradient(135deg, #ffffff, #a0aec0);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .reset-title {
          font-size: 26px;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 8px;
        }

        .reset-subtitle {
          color: rgba(255, 255, 255, 0.6);
          font-size: 14px;
          line-height: 1.6;
        }

        .reset-subtitle strong {
          color: #8b5cf6;
        }

        .reset-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .form-label {
          color: rgba(255, 255, 255, 0.8);
          font-size: 13px;
          font-weight: 500;
        }

        .input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .input-icon {
          position: absolute;
          left: 14px;
          color: rgba(255, 255, 255, 0.3);
          pointer-events: none;
        }

        .form-input {
          width: 100%;
          padding: 14px 14px 14px 46px;
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          color: #ffffff;
          font-size: 14px;
          transition: all 0.3s ease;
          outline: none;
        }

        .form-input::placeholder {
          color: rgba(255, 255, 255, 0.3);
        }

        .form-input:focus {
          border-color: #8b5cf6;
          background: rgba(255, 255, 255, 0.08);
          box-shadow: 0 0 0 4px rgba(139, 92, 246, 0.1);
        }

        .form-input:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .password-toggle {
          position: absolute;
          right: 14px;
          background: none;
          border: none;
          color: rgba(255, 255, 255, 0.3);
          cursor: pointer;
          padding: 4px;
          transition: color 0.3s ease;
          display: flex;
          align-items: center;
        }

        .password-toggle:hover {
          color: rgba(255, 255, 255, 0.6);
        }

        .password-toggle:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .password-strength {
          margin-top: 4px;
        }

        .strength-bar {
          height: 4px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 4px;
          overflow: hidden;
          margin-bottom: 4px;
        }

        .strength-fill {
          height: 100%;
          transition: width 0.3s ease, background-color 0.3s ease;
          border-radius: 4px;
        }

        .strength-label {
          font-size: 12px;
          font-weight: 500;
          transition: color 0.3s ease;
        }

        .password-hint {
          margin-top: 8px;
          padding: 12px;
          background: rgba(255, 255, 255, 0.03);
          border-radius: 8px;
        }

        .password-hint span {
          color: rgba(255, 255, 255, 0.4);
          font-size: 12px;
          display: block;
          margin-bottom: 4px;
        }

        .password-hint ul {
          list-style: none;
          padding: 0;
          margin: 0;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 4px 16px;
        }

        .password-hint li {
          color: rgba(255, 255, 255, 0.3);
          font-size: 12px;
          transition: all 0.3s ease;
        }

        .password-hint li.valid {
          color: #4ade80;
        }

        .password-match-error {
          color: #f87171;
          font-size: 12px;
          margin-top: 4px;
        }

        .password-match-success {
          color: #4ade80;
          font-size: 12px;
          margin-top: 4px;
        }

        .error-message {
          background: rgba(239, 68, 68, 0.15);
          border: 1px solid rgba(239, 68, 68, 0.2);
          border-radius: 10px;
          padding: 10px 14px;
          display: flex;
          align-items: center;
          animation: fadeIn 0.3s ease;
        }

        .error-message span {
          color: #f87171;
          font-size: 13px;
          font-weight: 500;
        }

        .success-message {
          background: rgba(34, 197, 94, 0.15);
          border: 1px solid rgba(34, 197, 94, 0.2);
          border-radius: 10px;
          padding: 10px 14px;
          display: flex;
          align-items: center;
          gap: 8px;
          animation: fadeIn 0.3s ease;
        }

        .success-message span {
          color: #4ade80;
          font-size: 13px;
          font-weight: 500;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .reset-btn {
          padding: 14px;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          border: none;
          border-radius: 12px;
          color: #ffffff;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }

        .reset-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(139, 92, 246, 0.3);
        }

        .reset-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .spinner {
          width: 18px;
          height: 18px;
          border: 2.5px solid rgba(255, 255, 255, 0.3);
          border-top-color: #ffffff;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        @media (max-width: 480px) {
          .reset-card {
            padding: 32px 24px;
          }
          .reset-title {
            font-size: 22px;
          }
          .logo-text {
            font-size: 18px;
          }
          .form-input {
            padding: 12px 12px 12px 42px;
            font-size: 13px;
          }
          .reset-btn {
            padding: 12px;
            font-size: 14px;
          }
          .password-hint ul {
            grid-template-columns: 1fr;
          }
          .bg-blob {
            opacity: 0.2;
          }
        }
      `}</style>
    </div>
  );
};

export default ResetPassword;